import * as PropTypes from 'prop-types';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as SampleConstants from 'constants/SampleConstants';
import BioentitySearchInput from 'inventory/BioentitySearchInput';
import BatchSearchInput from 'samples/batches/BatchSearchInput';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import LegacyDropdownMenu from 'ui-library/dropdowns/LegacyDropdownMenu';
import LegacyDropdownMenuItem from 'ui-library/dropdowns/LegacyDropdownMenuConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import runModal from 'ui-library/runModal';
import BulkAddWorkflowInputsModal from 'workflows/modals/BulkAddWorkflowInputsModal';
import {InputType} from 'workflows/stage-entries/components/ExternalInputPicker';

interface SearchInputItem {
  id: string;
  name: string;
}

export interface StageRunRow {
  batch?: SearchInputItem | null;
  file?: SearchInputItem | null;
  inputType: InputType;
}

interface WorkflowInputTableProps {
  initialStageRunRows: Array<StageRunRow>;
  initialStageRunRowsChanged: (stageRunRows: Array<StageRunRow | null>) => void;
}

interface State {
  isAddDropdownOpen: boolean;
}

export default class WorkflowInputTable extends React.Component<WorkflowInputTableProps, State> {
  static propTypes = {
    initialStageRunRows: PropTypes.arrayOf(
      PropTypes.shape({
        // At most one of batch or file should be specified
        batch: SampleConstants.BATCH_SUMMARY_SHAPE,
        file: PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
        }),
        inputType: PropTypes.oneOf([InputType.BATCH, InputType.FILE]).isRequired,
      }).isRequired
    ).isRequired,
    initialStageRunRowsChanged: PropTypes.func.isRequired,
  };

  override state: State = {
    isAddDropdownOpen: false,
  };

  _handleOpenAddDropdown = (): void => {
    this.setState({isAddDropdownOpen: true});
  };

  _handleCloseAddDropdown = (): void => {
    this.setState({isAddDropdownOpen: false});
  };

  _handleRowAdded(inputType: InputType): void {
    const newRow = {inputType};
    this.props.initialStageRunRowsChanged([...this.props.initialStageRunRows, newRow]);
  }

  _handleBatchChanged(index: number, batch: SearchInputItem | null): void {
    assert(this.props.initialStageRunRows[index].inputType === InputType.BATCH, 'Row is for selecting batch');
    const newRows = this.props.initialStageRunRows.map((row, i) => (i === index ? {...row, batch} : row));
    this.props.initialStageRunRowsChanged(newRows);
  }

  _handleFileChanged(index: number, file: SearchInputItem | null): void {
    assert(this.props.initialStageRunRows[index].inputType === InputType.FILE, 'Row is for selecting entity');
    const newRows = this.props.initialStageRunRows.map((row, i) => (i === index ? {...row, file} : row));
    this.props.initialStageRunRowsChanged(newRows);
  }

  _handleRowDeleted(index: number): void {
    const newRows = this.props.initialStageRunRows.filter((row, i) => i !== index);
    this.props.initialStageRunRowsChanged(newRows);
  }

  _handleBulkAddWorkflowInputs = async (): Promise<void> => {
    const importedSamples = await runModal(BulkAddWorkflowInputsModal, {});
    if (importedSamples) {
      const newRows = importedSamples.map((sample) =>
        sample.batchSummary
          ? {inputType: InputType.BATCH, batch: sample.batchSummary}
          : {inputType: InputType.FILE, file: sample.entitySummary}
      );
      const nonEmptyRows = this.props.initialStageRunRows.filter(
        ({batch, file}) => batch != null || file != null
      );
      this.props.initialStageRunRowsChanged([...nonEmptyRows, ...newRows]);
    }
  };

  _getAddMenuItems(): Array<Array<LegacyDropdownMenuItem>> {
    const items: Array<LegacyDropdownMenuItem> = [];
    if (BenchlingConfig.ENABLE_BATCHES) {
      items.push({
        name: 'Add batch',
        icon: 'compose',
        handleOnClick: () => {
          this._handleRowAdded(InputType.BATCH);
        },
      });
    }
    items.push({
      name: 'Add entity',
      icon: 'compose',
      handleOnClick: () => {
        this._handleRowAdded(InputType.FILE);
      },
    });
    items.push({
      name: 'Import from spreadsheet',
      icon: 'upload',
      handleOnClick: this._handleBulkAddWorkflowInputs,
    });
    return [items];
  }

  _renderHeader(): JSX.Element {
    return (
      <div className="simpleDivTable-headerCell">
        <div className="u-small-spacing-bottom u-vertical-center-items">
          <span>Input Samples</span>
          <AbsoluteDropdownContainer
            menu={
              <LegacyDropdownMenu items={this._getAddMenuItems()} className="workflowTable-addDropdown" />
            }
            onRequestClose={this._handleCloseAddDropdown}
            open={this.state.isAddDropdownOpen}
            shouldCloseOnInsideClick={true}
          >
            <BenchlingIcon
              className="workflowInputTable-addButton u-small-spacing-left"
              icon="compose"
              onClick={this._handleOpenAddDropdown}
            />
          </AbsoluteDropdownContainer>
        </div>
      </div>
    );
  }

  _renderDeleteButtonHeader(): JSX.Element {
    return <div className="workflowInputTable-deleteIcon-placeholder" />;
  }

  _renderRow(index: number, initializationInfo: StageRunRow): JSX.Element {
    return (
      <div className="simpleDivTable-row workflowConfigureScreen-deletable-row" key={index}>
        <div className="simpleDivTable-cell">
          {initializationInfo.inputType === InputType.BATCH ? (
            <BatchSearchInput
              autoFocus={false}
              onChange={(batches: Array<SearchInputItem>) => {
                this._handleBatchChanged(index, batches.length ? batches[0] : null);
              }}
              selectedBatches={initializationInfo.batch ? [initializationInfo.batch] : []}
            />
          ) : (
            <BioentitySearchInput
              onChange={(files: Array<SearchInputItem>) => {
                this._handleFileChanged(index, files.length ? files[0] : null);
              }}
              selectedItems={initializationInfo.file ? [initializationInfo.file] : []}
            />
          )}
        </div>
        <div className="workflowInputTable-deleteIcon-placeholder u-flex-display">
          <BenchlingIcon
            className="workflowConfigureScreen-delete faded u-danger-pointer"
            icon="delete"
            onClick={() => {
              this._handleRowDeleted(index);
            }}
          />
        </div>
      </div>
    );
  }

  override render(): JSX.Element {
    return (
      <div className="metadata-table simpleDivTable workflowInputTable">
        <div className="simpleDivTable-header">
          {this._renderHeader()}
          {this._renderDeleteButtonHeader()}
        </div>
        {(() => this.props.initialStageRunRows.map((row, i) => this._renderRow(i, row)))()}
      </div>
    );
  }
}
